import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraduationCap } from 'lucide-react';
import { publicAPI, schoolAPI } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from "@/contexts/AuthContext";
import { handleSubmit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import LocationEditor from "@/components/LocationEditor";
import ImageUpload from "@/components/ImageUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import MultiCheck	 from "@/components/MultiCheck";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import Section from "@/components/Section";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

const DISCIPLINES = ['dressage', 'show_jumping', 'vaulting', 'trail', 'western', 'classical', 'therapy'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'competition'];
const AGE_GROUPS = ['kids', 'teens', 'adults', 'all_ages'];
const FACILITIES = ['indoor_arena', 'outdoor_arena', 'cross_country', 'stabling', 'clubhouse'];
const LESSON_TYPES = ['private', 'group', 'semi_private'];
const PRICING_UNITS = ['lesson', 'hour', 'month', 'package'];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CreateRidingSchoolAdPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
	const { user } = useAuth();
	const [images, setImages] = useState([]);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);
	const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		pricing_unit: 'lesson',
		disciplines: [],
		levels: [],
		age_groups: [],
		facilities: [],
		lesson_types: [],
		has_school_horses: false,
		school_horses_count: '',
		certifications: '',
		company_name: '',
		website: '',
		contact_phone: '',
		location_name: '',
		location_lat: null,
		location_lng: null,
		is_featured: false,
		featured_paid: false,
		hidden_images: []
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		publicAPI.settings()
			.then(r => setPricing(
				prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			schoolAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
					pricing_unit: ad.pricing_unit || 'lesson',
					disciplines: ad.disciplines || [],
					levels: ad.levels || [],
					age_groups: ad.age_groups || [],
					facilities: ad.facilities || [],
					lesson_types: ad.lesson_types || [],
					has_school_horses: ad.has_school_horses || false,
					school_horses_count: ad.school_horses_count || '',
					certifications: ad.certifications || '',
					company_name: ad.company_name || '',
					website: ad.website || '',
					contact_phone: ad.contact_phone || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
				});

				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);

			}).catch((e) => {
				navigate('/dashboard')
			});
		}
	}, [user, editId, navigate]);

	const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

	const toggleIn = (key, value) => {
		setForm((p) => ({
			...p,
			[key]: p[key].includes(value) ? p[key].filter((x) => x !== value) : [...p[key], value],
		}));
	};

	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) => setImages(prev => prev.map((img, idx) =>
		idx === i ? { ...img, enabled: !img.enabled } : img));

	const submit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.description.trim() || !form.price) {
			toast.error(t('common.fill_required'));
			return;
		}

		const cost = computeCosts(images, [], pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);

		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);

		const payload = {
			...form,
			price: parseFloat(form.price),
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			school_horses_count: form.school_horses_count ? parseInt(form.school_horses_count, 10) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			hidden_images: hiddenImgs,
		};

		await handleSubmit(payload, schoolAPI, isClone ? null : editId, 'school', cost,
			newImages, [],	form.is_featured, form.featured_paid, setSaving, t, navigate);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="create-school-page">
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

				<BackLink title={form.title} uri="/schools" textKey='nav.back_to_results' />

				<div className="flex items-center gap-3 mb-2">
					<GraduationCap className="w-6 h-6 text-[#8B4513]" />
					<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{ isClone ? t('school.create_title') : editId ? t('school.edit_title') : t('school.create_title')}
					</h1>
				</div>
				<p className="text-sm text-[#666055] mb-6">{t('school.create_subtitle')}</p>

				<form onSubmit={submit} className="space-y-6"
							data-testid="school-form">
					{/* Basic Info */}
					<Section title={t('horse.basic_info')}>
						<div className="mb-4">
							<Label>{t('horse.title_label')} *</Label>
							<Input value={form.title} onChange={(e) => set('title', e.target.value)} required maxLength={120}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="school-title" />
						</div>

						<div className="mb-4">
							<Label>{t('horse.description')} *</Label>
							<Textarea value={form.description} onChange={(e) => set('description', e.target.value)}
												required rows={5} maxLength={4000}
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1"
												data-testid="school-description" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label>{t('horse.price')} (€) *</Label>
								<Input type="number" min={0} step="any" value={form.price}
											 onChange={(e) => set('price', e.target.value)} required
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="school-price" />
							</div>
							<div>
								<Label>{t('transport.pricing_unit')}</Label>
								<Select value={form.pricing_unit} onValueChange={(v) => set('pricing_unit', v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>{ PRICING_UNITS.map(u =>
										<SelectItem key={u} value={u}>{t(`school.unit.${u}`)}</SelectItem>)}
									</SelectContent>
								</Select>
							</div>
						</div>
					</Section>

					{/* Details */}
					<Section title={t('horse.details')}>
						<div className="mb-4">
							<MultiCheck label={t('school.disciplines')} items={DISCIPLINES} formKey="disciplines"
													prefix="school.discipline_opt" form={ form } toggleIn={ toggleIn }  t={ t } />
						</div>
						<div className="mb-4">
							<MultiCheck label={t('school.levels')} items={LEVELS} formKey="levels"
													prefix="school.level_opt" form={ form } toggleIn={ toggleIn }  t={ t } />
						</div>
						<div className="mb-4">
							<MultiCheck label={t('school.age_groups')} items={AGE_GROUPS} formKey="age_groups"
													prefix="school.age_opt" form={ form } toggleIn={ toggleIn }  t={ t } />
						</div>
						<div className="mb-4">
							<MultiCheck label={t('school.facilities')} items={FACILITIES} formKey="facilities"
													prefix="school.facility_opt" form={ form } toggleIn={ toggleIn }  t={ t } />
						</div>
						<div className="mb-4">
							<MultiCheck label={t('school.lesson_types')} items={LESSON_TYPES} formKey="lesson_types"
													prefix="school.lesson_type_opt" form={ form } toggleIn={ toggleIn }  t={ t } />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<label className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none mb-4">
								<Checkbox checked={form.has_school_horses} onCheckedChange={(v) => set('has_school_horses', !!v)}
													data-testid="school-has-horses" />
								{t('school.has_school_horses')}
							</label>
							{form.has_school_horses && (
								<div>
									<Label>{t('school.school_horses_count')}</Label>
									<Input type="number" min={0} value={form.school_horses_count}
												 onChange={(e) => set('school_horses_count', e.target.value)}
												 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
												 data-testid="school-horses-count" />
								</div>
							)}
						</div>

						<div className="mb-4">
							<Label>{t('school.certifications')}</Label>
							<Textarea value={form.certifications} onChange={(e) => set('certifications', e.target.value)}
												rows={2} maxLength={1000} placeholder="FEP affiliated, Pony Club, BHS..."
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<Label>{t('transport.company_name')}</Label>
								<Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
							<div>
								<Label>{t('school.website')}</Label>
								<Input value={form.website} onChange={(e) => set('website', e.target.value)}
											 placeholder="https://..." className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>

						<div>
							<Label>{t('horse.contact_phone')}</Label>
							<Input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
						</div>
					</Section>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ set }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ set } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						t={ t }/>

					<WaiverCheckBox
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ saving }
						label='school.publish_btn'
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						includedVideos={ form.included_videos }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

				</form>
			</div>
		</div>
	);
}